import {
  Box,
  type ColumnsType,
  Divider,
  Flex,
  Loading,
  Text,
  WarningIcon,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { usePriorIdFromLineage } from '@/features/project/_hooks/project';
import { useProject } from '@/features/project/_hooks/project';
import { useSubProjectsForKS } from '@/features/project/_hooks/sub-project';
import { isBefore2025 as isBefore2025Func } from '@/features/project/_utils/project';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import {
  getPreviousFundBalanceColumnData,
  getSimpleAmountColumnData,
  getSumOfTheEndOfThePriorFundBalance,
} from '@/features/project/projects/detail/execution-progress/_utils/execution-progress';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { AmountUnitLabelModifier, formatNumber, isPS } from '@/utils';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { SubProjectForKSModel } from '../../../_models/sub-project';
import { FundBusinessTable } from './FundBusinessTable';
import { IncomeAndExpenditureTable } from './IncomeAndExpenditureTable';
import { IndividualTable } from './IndividualTable';
import { SubProjectFundInfoFormDrawer } from './SubProjectFundInfoFormDrawer';
import { UpdateSubProjectIncomeAndExpenditureAndFundBusinessFormDrawer } from './UpdateSubProjectIncomeAndExpenditureAndFundBusinessFormDrawer';

type SubProjectForKs = z.infer<typeof SubProjectForKSModel>;
type SubProjectForKsColumns = ColumnsType<SubProjectForKs>;

type Project = z.infer<typeof ProjectModel>;
type Props = Pick<
  Project,
  | 'fiscalYear'
  | 'name'
  | 'fundBusinessForLocalGovernmentSummary'
  | 'fundAdditionalAttributes'
> & {
  editable: Project['isLocked'];
};

export const ExecutionProgressInformation: FC<Props> = ({
  fiscalYear,
  name: projectName,
  fundBusinessForLocalGovernmentSummary,
  fundAdditionalAttributes,
  editable,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const isBefore2025 = isBefore2025Func({ fiscalYear });
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const { id: projectId } = useId();
  const project = useProject(projectId);
  const subProjects = useSubProjectsForKS(projectId);
  const priorProjectId =
    usePriorIdFromLineage({
      params: {
        fiscalYear: project.data?.fiscalYear,
        lineageId: project.data?.lineageId ?? '',
      },
    }).data?.priorProjectId ?? '';
  // NOTE: 2025年度以前の内部サイトでは前年度の基金残高と今年度の基金残高が異なる場合はユーザーに知らせる
  const priorSubProjects = useSubProjectsForKS(
    isBefore2025 || isPS() ? '' : priorProjectId,
  );

  const previousFiscalYear = fiscalYear - 1;

  const getIncomeAndExpenditureColumns = useCallback(
    (type: 'individual' | 'summary'): SubProjectForKsColumns => {
      const isSummary = type === 'summary';
      const incomeAndExpenditureColumns: SubProjectForKsColumns = [
        {
          key: 'previousFundBalance',
          title: t('fund-balance-with-year', {
            fiscalYear: previousFiscalYear - 1,
          }),
          children: [
            {
              title: t('sum-amount'),
              align: 'end',
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'beginningOfTheFundBalance',
              ],
              render: (_, { fundBusinessForLocalGovernment, priorId }) => {
                const { value, showWarning, result } =
                  getPreviousFundBalanceColumnData({
                    isSimpleResponse: isBefore2025 || isPS() || isSummary,
                    isDebugMode,
                    targetValue:
                      fundBusinessForLocalGovernment?.beginningOfTheFundBalance,
                    targetNegativeValueCount:
                      fundBusinessForLocalGovernment?.negativeBeginningOfTheFundBalanceCount,
                    priorSubProjects: priorSubProjects.data,
                    priorId,
                    type: 'sum',
                  });
                if (
                  result === 'simpleResponse' ||
                  result === 'notPriorSubProject'
                ) {
                  return value;
                }
                return (
                  <Flex justify="end" gap={4}>
                    {showWarning && (
                      <WarningIcon
                        hint={t(
                          'warning-message-different-from-reference-value',
                        )}
                      />
                    )}
                    <div>{value}</div>
                  </Flex>
                );
              },
            },
            {
              title: t('amount-equivalent-to-national-expense'),
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'nationalEquivalentOfBeginningOfTheFundBalance',
              ],
              align: 'end',
              render: (_, { fundBusinessForLocalGovernment, priorId }) => {
                const { value, showWarning, result } =
                  getPreviousFundBalanceColumnData({
                    isSimpleResponse: isPS() || type === 'summary',
                    isDebugMode,
                    targetValue:
                      fundBusinessForLocalGovernment?.nationalEquivalentOfBeginningOfTheFundBalance,
                    targetNegativeValueCount:
                      fundBusinessForLocalGovernment?.negativeNationalEquivalentOfBeginningOfTheFundBalanceCount,
                    priorSubProjects: priorSubProjects.data,
                    priorId,
                    type: 'nationalExpense',
                  });
                if (
                  result === 'simpleResponse' ||
                  result === 'notPriorSubProject'
                ) {
                  return value;
                }
                return (
                  <Flex justify="end" gap={4}>
                    {showWarning && (
                      <WarningIcon
                        hint={t(
                          'warning-message-different-from-reference-value',
                        )}
                      />
                    )}
                    <div>{value}</div>
                  </Flex>
                );
              },
            },
          ],
        },
        {
          key: 'income',
          title: t('income-with-year', { year: previousFiscalYear }),
          children: [
            {
              title: t('sum-amount'),
              align: 'end',
              dataIndex: ['fundBusinessForLocalGovernment', 'incomeAmount'],
              render: (_, { fundBusinessForLocalGovernment }) =>
                getSimpleAmountColumnData({
                  value: fundBusinessForLocalGovernment?.incomeAmount,
                  negativeValueCount:
                    fundBusinessForLocalGovernment?.negativeIncomeAmountCount,
                  isDebugMode,
                }),
            },
            {
              title: t('amount-equivalent-to-national-expense'),
              align: 'end',
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'nationalEquivalentOfIncomeAmount',
              ],
              render: (_, { fundBusinessForLocalGovernment }) =>
                getSimpleAmountColumnData({
                  value:
                    fundBusinessForLocalGovernment?.nationalEquivalentOfIncomeAmount,
                  negativeValueCount:
                    fundBusinessForLocalGovernment?.negativeNationalEquivalentOfIncomeAmountCount,
                  isDebugMode,
                }),
            },
          ],
        },
        {
          key: 'amountOfFundingFromTheGovernment',
          title: t('amount-of-funding-from-the-government-with-year', {
            year: previousFiscalYear,
          }),
          children: [
            {
              title: t('initial'),
              align: 'end',
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'grantedAmountByTheCountryFromInitial',
              ],
              render: (_, { fundBusinessForLocalGovernment }) =>
                getSimpleAmountColumnData({
                  value:
                    fundBusinessForLocalGovernment?.grantedAmountByTheCountryFromInitial,
                  negativeValueCount:
                    fundBusinessForLocalGovernment?.negativeGrantedAmountByTheCountryFromInitialCount,
                  isDebugMode,
                }),
            },
            {
              title: t('correction'),
              align: 'end',
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'grantedAmountByTheCountryFromSupplementary',
              ],
              render: (_, { fundBusinessForLocalGovernment }) =>
                getSimpleAmountColumnData({
                  value:
                    fundBusinessForLocalGovernment?.grantedAmountByTheCountryFromSupplementary,
                  negativeValueCount:
                    fundBusinessForLocalGovernment?.negativeGrantedAmountByTheCountryFromSupplementaryCount,
                  isDebugMode,
                }),
            },
            {
              title: t('reserve-budget'),
              align: 'end',
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'grantedAmountByTheCountryFromReserve',
              ],
              render: (_, { fundBusinessForLocalGovernment }) =>
                getSimpleAmountColumnData({
                  value:
                    fundBusinessForLocalGovernment?.grantedAmountByTheCountryFromReserve,
                  negativeValueCount:
                    fundBusinessForLocalGovernment?.negativeGrantedAmountByTheCountryFromReserveCount,
                  isDebugMode,
                }),
            },
            ...(isSummary
              ? [
                  {
                    key: 'accountingType',
                    title: t('accounting-type'),
                    render: () => (
                      <Text
                        weight="regular"
                        text={fundAdditionalAttributes?.accountingTypes
                          .map((v) => v.name)
                          .join(', ')}
                      />
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          title: t('others-with-year', { year: previousFiscalYear }),
          align: 'end',
          dataIndex: ['fundBusinessForLocalGovernment', 'otherIncomeAmount'],
          render: (_, { fundBusinessForLocalGovernment }) =>
            getSimpleAmountColumnData({
              value: fundBusinessForLocalGovernment?.otherIncomeAmount,
              negativeValueCount:
                fundBusinessForLocalGovernment?.negativeOtherIncomeAmountCount,
              isDebugMode,
            }),
        },
        {
          title: t('expense-with-year', { year: previousFiscalYear }),
          align: 'end',
          dataIndex: ['fundBusinessForLocalGovernment', 'businessExpense'],
          render: (_, { fundBusinessForLocalGovernment }) =>
            getSimpleAmountColumnData({
              value: fundBusinessForLocalGovernment?.businessExpense,
              negativeValueCount:
                fundBusinessForLocalGovernment?.negativeBusinessExpenseCount,
              isDebugMode,
            }),
        },
        {
          title: t('national-treasury-return-with-year', {
            year: previousFiscalYear,
          }),
          align: 'end',
          dataIndex: [
            'fundBusinessForLocalGovernment',
            'amountReturnedToTheNationalTreasury',
          ],
          render: (_, { fundBusinessForLocalGovernment }) =>
            getSimpleAmountColumnData({
              value:
                fundBusinessForLocalGovernment?.amountReturnedToTheNationalTreasury,
              negativeValueCount:
                fundBusinessForLocalGovernment?.negativeAmountReturnedToTheNationalTreasuryCount,
              isDebugMode,
            }),
        },
        ...(isSummary
          ? [
              {
                key: 'numberOfLocalFundBuilding',
                title: t('number-of-local-fund-building-with-year', {
                  fiscalYear: previousFiscalYear,
                }),
                align: 'end',
                render: () => (
                  <Text
                    weight="regular"
                    text={formatNumber(
                      fundAdditionalAttributes?.numberOfLocalFundBuilding,
                    )}
                  />
                ),
              } satisfies SubProjectForKsColumns[number],
            ]
          : []),
        {
          key: 'currentFundBalance',
          title: t('fund-balance-with-year', {
            fiscalYear: previousFiscalYear,
          }),
          children: [
            {
              title: t('sum-amount'),
              align: 'end',
              render: (_, { fundBusinessForLocalGovernment }) => {
                if (!fundBusinessForLocalGovernment) {
                  return;
                }

                const { totalAmount, negativeCount } =
                  getSumOfTheEndOfThePriorFundBalance({
                    fundBusinessForLocalGovernment,
                  });
                return getSimpleAmountColumnData({
                  value: totalAmount,
                  negativeValueCount: negativeCount,
                  isDebugMode,
                });
              },
            },
            {
              title: t('amount-equivalent-to-national-expense'),
              align: 'end',
              dataIndex: [
                'fundBusinessForLocalGovernment',
                'nationalEquivalentOfTheEndOfTheFundBalance',
              ],
              render: (_, { fundBusinessForLocalGovernment }) =>
                getSimpleAmountColumnData({
                  value:
                    fundBusinessForLocalGovernment?.nationalEquivalentOfTheEndOfTheFundBalance,
                  negativeValueCount:
                    fundBusinessForLocalGovernment?.negativeNationalEquivalentOfTheEndOfTheFundBalanceCount,
                  isDebugMode,
                }),
            },
          ],
        },
      ];
      return incomeAndExpenditureColumns;
    },
    [
      t,
      previousFiscalYear,
      priorSubProjects,
      isBefore2025,
      fundAdditionalAttributes,
      isDebugMode,
    ],
  );

  const fundBusinessForLocalGovernmentSummaries = useMemo<
    SubProjectForKs[] | undefined
  >(() => {
    const subProject = subProjects.data?.at(0);
    if (!fundBusinessForLocalGovernmentSummary || !subProject) {
      return undefined;
    }
    return [
      {
        ...subProject,
        fundBusinessForLocalGovernment: fundBusinessForLocalGovernmentSummary,
      },
    ];
  }, [fundBusinessForLocalGovernmentSummary, subProjects.data]);

  if (subProjects.isPending) {
    return <Loading />;
  }

  return (
    <Flex vertical gap={16}>
      <Text
        variant="h5"
        text={t('summary-table-with-unit', {
          unit: AmountUnitLabelModifier[unit],
        })}
      />
      <Box>
        <Box marginBottom={16}>
          <IncomeAndExpenditureTable
            fundBusinessForLocalGovernmentSummaries={
              fundBusinessForLocalGovernmentSummaries
            }
            columns={getIncomeAndExpenditureColumns('summary')}
            editable={editable}
          />
        </Box>
        <Box paddingBottom={8}>
          <FundBusinessTable
            fundBusinessForLocalGovernmentSummaries={
              fundBusinessForLocalGovernmentSummaries
            }
            previousFiscalYear={previousFiscalYear}
          />
        </Box>
        <Divider size="lg" />
        <IndividualTable
          individualColumns={getIncomeAndExpenditureColumns('individual')}
          previousFiscalYear={previousFiscalYear}
          projectName={projectName}
          fiscalYear={fiscalYear}
        />
      </Box>
      <SubProjectFundInfoFormDrawer />
      <UpdateSubProjectIncomeAndExpenditureAndFundBusinessFormDrawer
        fiscalYear={fiscalYear}
      />
    </Flex>
  );
};
